<template>
  <dialog-modal-fi
    ref="dialog"
    class="media-download-dialog-fi modal-fi--content-width-sm modal-fi--action-buttons modal-fi--mobile-fullscreen"
    lazy
    @before-close="beforeClose"
  >
    <template #header>
      <richtext-fi
        v-if="fields?.mediaDownloadDialogText"
        class="media-download-dialog-fi__text"
        :html-content="fields.mediaDownloadDialogText"
      />
    </template>
    <template #footer>
      <div class="media-download-dialog-fi__button">
        <loading-indicator-wrapper
          :loading="isLoading"
          variant="spinnerLocal"
          spinoff="in-relative-centered"
        >
          <a
            download
            :disabled="isLoading"
            class="button-fi__tertiary--large button-fi--icon button-fi__xs-streched"
            @click="downloadMedia(mediaDownloadLink)"
          >
            <icon-fi icon="download" />
            {{ fields?.mediaDownloadDialogButton?.value }}
          </a>
        </loading-indicator-wrapper>
      </div>
    </template>
  </dialog-modal-fi>
</template>

<script setup lang="ts">
import { DialogModalFi, IconFi, LoadingIndicatorWrapper, RichtextFi } from 'atoms';
import { ref, useTemplateRef } from 'vue';

import { useCookies } from '@/composables/cookies';
import { sendEventFunction } from '@/composables/eventHub';
import { useFischer } from '@/composables/fischerPlugin';
import type { RichTextField, SingleLineTextField } from '@/sitecoreFieldTypes';
import { useNotificationStore } from '@/store/pinia/notification';

// dynamic import because static import is not SSR compatible
const importFileSaver = () => import('file-saver');

interface MediaDownloadFields {
  mediaDownloadDialogText?: RichTextField;
  mediaDownloadDialogButton?: SingleLineTextField;
  mediaDownloadDialogErrorText?: SingleLineTextField;
}

interface Props {
  fields: MediaDownloadFields | null | undefined;
  mediaDownloadLink?: string | null;
  fileName?: string | null;
}

defineOptions({
  name: 'MediaDownloadDialogFi',
});

const props = withDefaults(defineProps<Props>(), {
  mediaDownloadLink: null,
  fileName: '',
});

const fischer = useFischer();
const { cookies } = useCookies();
const { openToastNotification } = useNotificationStore();

const dialog = useTemplateRef('dialog');

const isLoading = ref(false);
const timer = ref<ReturnType<typeof setTimeout> | null>(null);
const attempts = ref(0);

const show = () => {
  dialog.value?.open();
};

const beforeClose = () => {
  isLoading.value = false;
  if (timer.value) {
    clearTimer();
  }
};

const close = () => {
  dialog.value?.close();
};

const downloadMedia = async (url?: string | null) => {
  if (IS_SSR || !url || !props.fileName) {
    return;
  }

  isLoading.value = true;
  sendEventFunction(fischer, cookies, 'custom', {}, 'download', {
    link: url,
    name: props.fileName,
  });

  const [response, FileSaver] = await Promise.all([fetch(url), importFileSaver()]);

  if (response?.status === 200) {
    isLoading.value = false;

    const fileName = `media-${props.fileName.replace(/\s+/g, '-')}.zip`;
    FileSaver.default.saveAs(await response.blob(), fileName);
  } else {
    timeoutAndTryAgain();
  }
};

const timeoutAndTryAgain = () => {
  if (attempts.value < 9) {
    timer.value = setTimeout(() => {
      attempts.value += 1;
      downloadMedia(props.mediaDownloadLink);
    }, 1000);
  } else {
    handleErrorNotification();
  }
};

const clearTimer = () => {
  if (timer.value) {
    clearTimeout(timer.value);
    timer.value = null;
    attempts.value = 0;
  }
};

const handleErrorNotification = () => {
  isLoading.value = false;
  close();

  openToastNotification({
    message: props.fields?.mediaDownloadDialogErrorText,
    type: 'error',
    icon: 'alert-triangle',
    persist: true,
  });
};

defineExpose({
  show,
  close,
});
</script>

<style src="./media-download-dialog-fi" />
